import {
  isCookieMessageClosable,
  isCookieMessageOpen,
  isCookieMessageSettingsOpen,
  getDeviceType
} from "../../state/selectors";
import React from "react";
import { FormattedMessage, injectIntl, InjectedIntlProps } from "react-intl";
import { connect } from "react-redux";
import * as cookieMessageActions from "../../state/actions/cookieMessage.actions";
import { COOKIE_STANDARD } from "../../utils/cookieMessage.utils";
import Modal from "../shared/modal/Modal";
import Intro from "./cookie-message-intro/CookieMessageIntro";
import Settings from "./cookie-message-settings/CookieMessage.settings";
import "./CookieMessage.scss";

interface CookieMessageProps {
  toggleSettings(): void;
  isOpen: boolean;
  isSettingsOpen: boolean;
  isClosable: boolean;
  deviceType: string;
  closePopup(): void;
  closePopupAndSave(): void;
}

/**
 * The CookieMessage components shows a cookie message at the bottom of the screen. The message can be closed by
 * clicking on the close button.
 */
class CookieMessage extends React.Component<
  CookieMessageProps & InjectedIntlProps,
  any
> {
  static displayName = "CookieMessage";

  /**
   * The anchors that toggle between Settings and Intro are coming from the CMS.
   * For this reason we have to find an anchor without href attribute and toggle
   * the states manually.
   */
  toggleSettingsOnClick = e => {
    let el = e.target;
    while (el && el.nodeName !== "A") {
      el = el.parentNode;
    }

    if (!el) {
      return;
    }

    const href = el.getAttribute("href");
    if (!href) {
      e.nativeEvent.preventDefault();
      this.props.toggleSettings();
    }
  };

  componentDidMount() {
    if (this.props.isOpen) {
      document.body.classList.add("no-scroll");
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.isOpen !== prevProps.isOpen) {
      if (this.props.isOpen) {
        document.body.classList.add("no-scroll");
      } else {
        document.body.classList.remove("no-scroll");
      }
    }
  }

  componentWillUnmount() {
    document.body.classList.remove("no-scroll");
  }

  render() {
    const {
      isOpen,
      isSettingsOpen,
      isClosable,
      closePopupAndSave,
      deviceType,
      closePopup,
      intl
    } = this.props;
    const translation = isSettingsOpen ? "settings" : "intro";

    return (
      <Modal
        open={true}
        onClose={isClosable ? closePopup : undefined}
        header={<FormattedMessage id={`CookieMessage.${translation}.title`} />}
      >
        <div
          role="presentation"
          onClick={this.toggleSettingsOnClick}
          className="cookie-message"
        >
          <div className="cookie-message__body">
            {isSettingsOpen ? (
              <Settings
                intl={intl}
                closePopupAndSave={closePopupAndSave}
                deviceType={deviceType}
              />
            ) : (
              <Intro
                intl={intl}
                closePopupAndSave={closePopupAndSave}
                deviceType={deviceType}
              />
            )}
          </div>
        </div>
      </Modal>
    );
  }
}

export const mapStateToProps = state => ({
  isOpen: isCookieMessageOpen(state),
  isClosable: isCookieMessageClosable(state),
  isSettingsOpen: isCookieMessageSettingsOpen(state),
  deviceType: getDeviceType(state)
});

export const mapDispatchToProps = dispatch => ({
  closePopup: () => dispatch(cookieMessageActions.closePopup()),
  closePopupAndSave: (cookieValue = COOKIE_STANDARD) =>
    dispatch(cookieMessageActions.closePopupAndSave(cookieValue)),
  toggleSettings: () => dispatch(cookieMessageActions.toggleSettings())
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(injectIntl(CookieMessage));
